import * as Flashphoner from '@flashphoner/websdk';
import * as RoomApi from '@flashphoner/websdk/src/room-module.js';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import ExpiredPage from 'components/rescue/ExpiredPage';
import { IStoreState } from 'store/modules';
import * as FPUtils from '../modules/fp-utils';
import { HiddenAudioPlayer, VIEWER_PREFIX, isViewerName } from '../modules/hidden-audio';
import { ICE_MEDIA_OPTIONS } from '../modules/ice-config';
import ViewerList from './ViewerList';
import ViewerPlayer, { VIDEO_CONTAINER_ID } from './ViewerPlayer';
import { Publisher } from './types';

const SESSION_STATUS = Flashphoner.constants.SESSION_STATUS;
const STREAM_STATUS = Flashphoner.constants.STREAM_STATUS;
const ROOM_EVENT = RoomApi.events;
const Browser = Flashphoner.Browser;
const PRELOADER_URL = process.env.PUBLIC_URL + '/media/preloader.mp4';
const SERVER_URL = 'wss://smartsar.kr/1/ws';
const LOCAL_DISPLAY_ID = 'viewerLocalDisplay';

export const VIEWER_EXPIRED_MESSAGE = (
  <>
    종료된 사건입니다.
    <br />
    실시간 영상은 제공되지 않습니다.
  </>
);

type ViewerStatus = 'connecting' | 'live' | 'failed' | 'expired';

interface ViewerTemplateProps {
  room: string;
}

const isCenterName = (name: string) => name.includes('center__');
const isPublisherName = (name: string) => !isCenterName(name) && !isViewerName(name);
const isHiddenAudioName = (name: string) => isCenterName(name) || isViewerName(name);

const ViewerTemplate = ({ room }: ViewerTemplateProps) => {
  const curRoom: any = useSelector((store: IStoreState) => store.MainReducer.curRoom);
  const [status, setStatus] = useState<ViewerStatus>('connecting');
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [selectedName, setSelectedName] = useState<string | undefined>();
  const [started, setStarted] = useState(false);
  const sessionRef = useRef<any>(null);
  const roomRef = useRef<any>(null);
  const statusRef = useRef<ViewerStatus>('connecting');
  const usernameRef = useRef(VIEWER_PREFIX + FPUtils.createUUID(8));
  const startedRef = useRef(false);
  const publishStreamRef = useRef<any>(null);
  const hiddenAudioRef = useRef(new HiddenAudioPlayer());

  const updateStatus = (next: ViewerStatus) => {
    statusRef.current = next;
    setStatus(next);
  };

  const upsertPublisher = (participant: any) => {
    const name = participant.name();
    if (!isPublisherName(name)) return;
    const streams = participant.getStreams();
    if (streams.length === 0) return;
    const stream = streams[0];
    const streamId = typeof stream.streamName === 'function' ? stream.streamName() : stream.id();
    const publisher: Publisher = { name, participant, streamId };
    setPublishers((prev) => {
      const index = prev.findIndex((p) => p.name === name);
      if (index < 0) return [...prev, publisher];
      const next = [...prev];
      next[index] = publisher;
      return next;
    });
    setSelectedName((prev) => prev ?? name);
  };

  const removePublisher = (name: string) => {
    setPublishers((prev) => prev.filter((p) => p.name !== name));
  };

  const playHiddenAudio = (participant: any) => {
    const name = participant.name();
    if (!isHiddenAudioName(name) || name === usernameRef.current) return;
    if (!startedRef.current) return;
    hiddenAudioRef.current.play(participant);
  };

  const publishLocalMedia = (joinedRoom: any) => {
    const display = document.getElementById(LOCAL_DISPLAY_ID);
    joinedRoom
      .publish({
        display,
        constraints: { audio: true, video: false },
        record: false,
        receiveVideo: false,
        receiveAudio: false,
      })
      .on(STREAM_STATUS.PUBLISHING, (stream: any) => {
        publishStreamRef.current = stream;
      })
      .on(STREAM_STATUS.UNPUBLISHED, () => {
        publishStreamRef.current = null;
      })
      .on(STREAM_STATUS.FAILED, () => {
        console.warn('Viewer audio publish failed');
        publishStreamRef.current = null;
      });
  };

  const leaveAndDisconnect = () => {
    const currentRoom = roomRef.current;
    const session = sessionRef.current;
    const publishStream = publishStreamRef.current;
    roomRef.current = null;
    sessionRef.current = null;
    publishStreamRef.current = null;
    hiddenAudioRef.current.stopAll();
    if (publishStream) {
      try {
        publishStream.stop();
      } catch (error) {
        //
      }
    }
    const disconnect = () => {
      if (session) session.disconnect();
    };
    if (currentRoom) {
      currentRoom.leave().then(disconnect, disconnect);
    } else {
      disconnect();
    }
  };

  const onExpire = () => {
    updateStatus('expired');
    leaveAndDisconnect();
  };

  const onSessionLost = () => {
    if (statusRef.current === 'expired') return;
    updateStatus('failed');
    roomRef.current = null;
    sessionRef.current = null;
  };

  const joinRoom = (session: any) => {
    session
      .join({ name: room, record: false })
      .on(ROOM_EVENT.STATE, (joinedRoom: any) => {
        roomRef.current = joinedRoom;
        joinedRoom.getParticipants().forEach((participant: any) => {
          upsertPublisher(participant);
          playHiddenAudio(participant);
        });
        updateStatus('live');
        const display = document.getElementById(LOCAL_DISPLAY_ID);
        if (Browser.isSafariWebRTC() && display) {
          Flashphoner.playFirstVideo(display, true, PRELOADER_URL).then(() => publishLocalMedia(joinedRoom));
          return;
        }
        publishLocalMedia(joinedRoom);
      })
      .on(ROOM_EVENT.PUBLISHED, (participant: any) => {
        upsertPublisher(participant);
        playHiddenAudio(participant);
      })
      .on(ROOM_EVENT.LEFT, (participant: any) => {
        const name = participant.name();
        removePublisher(name);
        hiddenAudioRef.current.stop(name);
      })
      .on(ROOM_EVENT.FAILED, (failedRoom: any, info: string) => {
        console.log('ROOM FAILED: ' + info);
        onSessionLost();
        session.disconnect();
      })
      .on(ROOM_EVENT.MESSAGE, (message: any) => {
        if (!message.text) return;
        try {
          const received = JSON.parse(decodeURIComponent(message.text));
          if (received.type === 'Expire') {
            onExpire();
          }
        } catch (error) {
          //
        }
      });
  };

  const createConnection = () => {
    const options = {
      urlServer: SERVER_URL,
      username: usernameRef.current,
      mediaOptions: ICE_MEDIA_OPTIONS,
    };
    RoomApi.connect(options)
      .on(SESSION_STATUS.ESTABLISHED, (session: any) => {
        sessionRef.current = session;
        joinRoom(session);
      })
      .on(SESSION_STATUS.DISCONNECTED, () => {
        onSessionLost();
      })
      .on(SESSION_STATUS.FAILED, () => {
        onSessionLost();
      });
  };

  useEffect(() => {
    try {
      Flashphoner.init({});
      createConnection();
    } catch (error) {
      console.log('Flashphoner init Error ', error);
      updateStatus('failed');
    }
    return () => {
      leaveAndDisconnect();
    };
  }, []);

  const startPlayback = () => {
    startedRef.current = true;
    setStarted(true);
    const currentRoom = roomRef.current;
    if (currentRoom) {
      currentRoom.getParticipants().forEach(playHiddenAudio);
    }
  };

  const onStart = () => {
    const container = document.getElementById(VIDEO_CONTAINER_ID);
    if (Browser.isSafariWebRTC() && container) {
      Flashphoner.playFirstVideo(container, false, PRELOADER_URL).then(startPlayback);
      return;
    }
    startPlayback();
  };

  if (status === 'expired') {
    return <ExpiredPage message={VIEWER_EXPIRED_MESSAGE} />;
  }

  const selected = publishers.find((p) => p.name === selectedName);
  const selectedEnded = selectedName !== undefined && selected === undefined;

  return (
    <ViewerTemplateWrap>
      <LocalDisplay id={LOCAL_DISPLAY_ID} />
      <HeaderWrap>
        <FaviconImg src={process.env.PUBLIC_URL + '/favicon.ico'} alt="" />
        <TitleWrap>
          해양사고일시 : {curRoom?.accDate} {curRoom?.accTime}
        </TitleWrap>
      </HeaderWrap>
      <BodyWrap>
        <ViewerList publishers={publishers} selectedName={selectedName} onSelect={setSelectedName} />
        <PlayerWrap>
          <ViewerPlayer publisher={selected} started={started} onEnded={removePublisher} />
          {status === 'connecting' && <Overlay>연결 중...</Overlay>}
          {status === 'failed' && (
            <Overlay>
              연결이 끊어졌습니다.
              <br />
              새로고침 후 다시 시도해 주세요.
            </Overlay>
          )}
          {status === 'live' && publishers.length === 0 && !selectedEnded && (
            <Overlay>전송 중인 영상이 없습니다.</Overlay>
          )}
          {status === 'live' && selectedEnded && (
            <Overlay>
              전송이 종료되었습니다.
              <br />
              <SubText>다른 전송자를 선택해 주세요.</SubText>
            </Overlay>
          )}
          {status === 'live' && !started && selected !== undefined && (
            <Overlay>
              <PlayButton onClick={onStart}>
                <img src={process.env.PUBLIC_URL + '/images/btn_play.svg'} alt="재생" />
              </PlayButton>
              <SubText>재생을 누르면 영상과 음성이 시작됩니다.</SubText>
            </Overlay>
          )}
        </PlayerWrap>
      </BodyWrap>
    </ViewerTemplateWrap>
  );
};

const ViewerTemplateWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #002343;
  color: #ffffff;
`;
const LocalDisplay = styled.div`
  width: 1px;
  height: 0;
  overflow: hidden;
`;
const HeaderWrap = styled.div`
  display: flex;
  align-items: center;
  height: 3.5rem;
  padding: 0 1rem;
  background: linear-gradient(180deg, #1760a6 0%, #010c16 23.08%, #010c16 79.33%, #1760a6 100%);
  border-bottom: 1px solid #000;
  gap: 0.75rem;
`;
const FaviconImg = styled.img`
  flex: 0 0 auto;
  width: 1.5rem;
  height: 1.5rem;
`;
const TitleWrap = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 1rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const BodyWrap = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;
const PlayerWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  background: #000;
`;
const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  background: rgba(0, 0, 0, 0.6);
  font-size: 1.1rem;
  font-weight: 500;
  line-height: 1.6;
  text-align: center;
  z-index: 2;
`;
const SubText = styled.span`
  font-size: 18px;
  font-weight: 400;
  color: #b8c7d9;
`;
const PlayButton = styled.button`
  width: 5rem;
  height: 5rem;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  img {
    width: 100%;
    height: 100%;
  }
`;

export default ViewerTemplate;
